import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { User, LogOut, Activity, Shield } from "lucide-react";
import AuthorAvatar from "@/components/AuthorAvatar";
import { useAuth } from "@/context/AuthContext";

/**
 * AccountMenu — the top-right avatar dropdown that replaces the primary
 * "Profile" nav tab. Contains the user's identity + secondary destinations
 * (Profile, Tracking, Moderation for admin) + Sign out. Kept intentionally
 * simple: five items maximum so the panel stays calm.
 */
export default function AccountMenu({ profile }) {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const nav = useNavigate();

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("touchstart", onDoc, { passive: true });
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("touchstart", onDoc);
    };
  }, [open]);

  const go = (path) => { setOpen(false); nav(path); };

  const displayName =
    profile?.display_name ||
    profile?.name ||
    user?.name ||
    "You";

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="account-menu-trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        className="flex items-center gap-2 rounded-full pl-1 pr-3 py-1 border hairline hover:bg-[hsl(var(--sand))]/60 tab-link"
      >
        <AuthorAvatar
          name={displayName}
          src={profile?.avatar_url || user?.picture}
          size="sm"
          ring="clay"
          testid="account-menu-avatar"
        />
        <span className="hidden md:inline text-sm text-[hsl(var(--fg))]/85 max-w-[110px] truncate">
          {displayName}
        </span>
      </button>

      {open && (
        <div
          role="menu"
          data-testid="account-menu-panel"
          className="absolute right-0 top-[calc(100%+8px)] w-64 rounded-md border hairline bg-[hsl(var(--paper))] shadow-lg p-2 z-50"
        >
          <div className="px-3 py-3 border-b hairline">
            <div className="font-display text-lg leading-tight" data-testid="account-menu-name">
              {displayName}
            </div>
            <div className="text-xs text-[hsl(var(--muted-foreground))] truncate" data-testid="account-menu-email">
              {user?.email}
            </div>
            {user?.role === "admin" && (
              <div className="mt-1 text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--terracotta))]">
                Admin
              </div>
            )}
          </div>
          <MenuItem
            icon={User}
            label="Profile & settings"
            onClick={() => go("/profile")}
            testid="account-menu-profile"
          />
          {user?.role === "admin" && (
            <MenuItem
              icon={Shield}
              label="Moderation queue"
              onClick={() => go("/moderation")}
              testid="account-menu-moderation"
            />
          )}
          <div className="my-1 border-t hairline" />
          <MenuItem
            icon={LogOut}
            label="Sign out"
            onClick={() => { setOpen(false); logout(); }}
            testid="account-menu-logout"
            danger
          />
        </div>
      )}
    </div>
  );
}

function MenuItem({ icon: Icon, label, onClick, testid, danger }) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      data-testid={testid}
      className={`w-full flex items-center gap-3 px-3 py-2 text-left text-sm rounded tab-link ${
        danger
          ? "text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--terracotta))] hover:bg-[hsl(var(--sand))]/60"
          : "text-[hsl(var(--fg))]/85 hover:bg-[hsl(var(--sand))]/60"
      }`}
    >
      <Icon size={15} className="opacity-80" />
      {label}
    </button>
  );
}
